import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

const MAX_VISIBLE_FEEDBACK = 4;
const DEFAULT_DURATION_MS = 4600;

const TYPE_META = {
  success: { label: "OK", title: "Hecho" },
  error: { label: "!", title: "No se pudo completar" },
  warning: { label: "!", title: "Atención" },
  info: { label: "i", title: "Información" },
  achievement: { label: "XP", title: "Logro desbloqueado" },
  level: { label: "LVL", title: "Subida de nivel" },
};

function normalizeFeedback(raw = {}) {
  const type = TYPE_META[raw.type] ? raw.type : "info";
  const meta = TYPE_META[type];
  const title = String(raw.title || meta.title || "Información").trim();
  const message = String(raw.message || raw.text || "").trim();
  const actionLabel = String(raw.actionLabel || "").trim();
  const actionTo = String(raw.actionTo || "").trim();
  const durationMs = Number.isFinite(Number(raw.durationMs))
    ? Math.max(0, Number(raw.durationMs))
    : DEFAULT_DURATION_MS;

  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    type,
    label: meta.label,
    title,
    message,
    actionLabel,
    actionTo,
    durationMs,
  };
}

export function emitGlobalFeedback(detail) {
  if (typeof window === "undefined") return false;
  const event = new CustomEvent("xphub:feedback", {
    detail,
    cancelable: true,
  });
  window.dispatchEvent(event);
  return event.defaultPrevented;
}

export default function GlobalFeedback() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const timersRef = useRef(new Map());
  const recentRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) window.clearTimeout(timer);
    timersRef.current.delete(id);
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const pushFeedback = useCallback(
    (raw) => {
      const item = normalizeFeedback(raw);
      const signature = `${item.type}|${item.title}|${item.message}`;
      const now = Date.now();
      const lastSeen = recentRef.current.get(signature) || 0;
      if (now - lastSeen < 1800) return;
      recentRef.current.set(signature, now);

      setItems((prev) => [item, ...prev].slice(0, MAX_VISIBLE_FEEDBACK));
      if (item.durationMs > 0) {
        const timer = window.setTimeout(() => dismiss(item.id), item.durationMs);
        timersRef.current.set(item.id, timer);
      }
    },
    [dismiss]
  );

  useEffect(() => {
    const activeTimers = timersRef.current;
    const onFeedback = (event) => {
      event.preventDefault();
      pushFeedback(event.detail || {});
    };
    window.addEventListener("xphub:feedback", onFeedback);
    return () => {
      window.removeEventListener("xphub:feedback", onFeedback);
      activeTimers.forEach((timer) => window.clearTimeout(timer));
      activeTimers.clear();
    };
  }, [pushFeedback]);

  const handleAction = useCallback(
    (item) => {
      dismiss(item.id);
      if (!item.actionTo) return;
      if (/^https?:\/\//i.test(item.actionTo)) {
        window.location.assign(item.actionTo);
        return;
      }
      navigate(item.actionTo);
    },
    [dismiss, navigate]
  );

  return (
    <div className="global-feedback-stack" aria-live="polite" aria-relevant="additions">
      <AnimatePresence initial={false}>
        {items.map((item) => (
          <motion.article
            key={item.id}
            className={`global-feedback global-feedback--${item.type}`}
            role={item.type === "error" || item.type === "warning" ? "alert" : "status"}
            initial={{ opacity: 0, x: 40, scale: 0.94, filter: "blur(4px)" }}
            animate={{ opacity: 1, x: 0, scale: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, x: 28, scale: 0.96, filter: "blur(2px)" }}
            transition={{ type: "spring", duration: 0.42, bounce: 0 }}
            layout
          >
            <div className="global-feedback__badge" aria-hidden="true">
              {item.label}
            </div>
            <div className="global-feedback__body">
              <strong>{item.title}</strong>
              {item.message ? <span>{item.message}</span> : null}
              {item.actionLabel && item.actionTo ? (
                <button type="button" onClick={() => handleAction(item)} className="global-feedback__action">
                  {item.actionLabel}
                </button>
              ) : null}
            </div>
            <button
              type="button"
              className="global-feedback__close"
              onClick={() => dismiss(item.id)}
              aria-label="Cerrar aviso"
            >
              x
            </button>
          </motion.article>
        ))}
      </AnimatePresence>
    </div>
  );
}
